import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clapperboard, Film, Headset, LayoutGrid, MessageCircle, MoreHorizontal, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDeleteDialog } from '../../../../components/design-system/common/ConfirmDeleteDialog';
import { Spinner } from '@/components/ui/spinner';
import { CreditBadge } from '../../content-studio/components/CreditBadge';
import { ProductPickerPanel } from '../../content-studio/components/ProductPickerPanel';
import { VideoEditorModal } from '../../content-studio/video-editor/VideoEditorModal';

import { newShotId } from '../board-ops';
import { StudioShot, isTakeShot, photoIsDesigned } from '../studio-contract';
import { useProduction } from '../use-production';
import { ArdiProposal, useArdi } from '../use-ardi';
import { useFilm } from '../use-film';
import { useReference } from '../use-reference';
import { bakeOverlay, hasOverlay, overlayKey } from '../overlay-design';
import { captureKey, clipIn3d, clipSize, move3dFor, needsCapture, objectAngleFor } from '../surface-shots';
import { Surface3DStudioWidget } from '../../content-studio/canvas/Surface3DStudioWidget';
import { rememberProducts, useProductInfo } from '../use-product-info';
import { shotClipPrice, takePrice, useStudioApi } from '../use-studio-api';
import { ArdiPanel } from './ArdiPanel';
import { ComposerChip } from './Composer';
import { ProductInfo, ShotCard, productInfoFrom } from './ShotCard';
import { ShotInspector } from './ShotInspector';
import { ReferenceStrip } from './ReferenceStrip';
import { ReferenceScan, ScanStage } from './ReferenceScan';
import { FormatMenu, LookMenu } from './BoardSettings';
import { FilmLightingButton } from './LightingReference';
import { ShotPreview } from './ShotPreview';
import { ImageEditorModal } from '../../content-studio/components/ImageEditorModal';
import { DesignEditorModal } from '../../content-studio/components/design-editor/DesignEditorModal';
import { editImageRequest } from '../../content-studio/edit-image-recipes';
import { angleRequest, EditorTool } from '../edit-actions';
import { CsAsset } from '../../../../common/models/content-studio.model';
import { openSupport } from '../../../support/vendor/support-widget.store';

export interface StudioStartState {
    message?: string;
    products?: ProductInfo[];
    referenceFile?: File;
}

/**
 * A production: Ardi on the left, the shots in the middle, the selected shot
 * on the right.
 *
 * Built so the fastest path needs no settings at all — say what you want, the
 * photos appear, press "Film it" on the ones you like, "Make the film" — and
 * every setting is still one click away on the card for whoever wants control.
 */
export const ProductionPage: React.FC<{ projectUuid: string }> = ({ projectUuid }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const prod = useProduction(projectUuid);
    const studioApi = useStudioApi();
    const { board, setBoard, boardRef } = prod;
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const nameRef = useRef<HTMLInputElement>(null);
    // The photo being edited in the image editor, and with which tool.
    const [editing, setEditing] = useState<{ shotId: string; tool: EditorTool; asset: CsAsset } | null>(null);
    const [editPrices, setEditPrices] = useState<Record<string, number>>({});
    // A full-screen workspace: the floating support button would sit on the
    // inspector's buttons, so it steps aside here (support is in the ⋯ menu).
    useEffect(() => {
        document.body.setAttribute('data-hide-support-fab', '');
        return () => document.body.removeAttribute('data-hide-support-fab');
    }, []);
    useEffect(() => {
        studioApi.listRecipes()
            .then((rs: any[]) => setEditPrices(Object.fromEntries((rs || []).map((r) => [r.slug, Math.ceil(r.creditCost || 0)]))))
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const openEditor = useCallback(async (shotId: string, tool: EditorTool) => {
        const s = boardRef.current.shots.find((x) => x.id === shotId);
        if (!s?.still?.assetUuid) return;
        try {
            const asset = await studioApi.getAsset(s.still.assetUuid);
            setEditing({ shotId, tool, asset });
        } catch { /* the asset is gone; nothing to edit */ }
    }, [studioApi, boardRef]);
    // Design: the layered editor. A version made there reopens with its layers.
    const [designing, setDesigning] = useState<{ shotId: string; backgroundUrl: string; fabricJson?: any } | null>(null);
    const [brand, setBrand] = useState<{ logoUrl?: string; colors?: string[]; name?: string }>({});
    useEffect(() => {
        studioApi.getBrandKit()
            .then((kit: any) => setBrand({ logoUrl: kit?.logoUrl || undefined, name: kit?.brandName || undefined, colors: [...(kit?.primaryColors || []), ...(kit?.secondaryColors || [])] }))
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const openDesign = useCallback((shotId: string) => {
        const s = boardRef.current.shots.find((x) => x.id === shotId);
        if (!s?.still?.url) return;
        const resume = s.design && s.design.assetUuid === s.still.assetUuid;
        setDesigning({ shotId, backgroundUrl: resume ? s.design!.backgroundUrl : s.still.url, fabricJson: resume ? s.design!.fabricJson : undefined });
    }, [boardRef]);
    // A photo shot's title and graphics are drawn on its photo as soon as it
    // is ready, and drawn again when they change — as layers Design reopens.
    // A redraw replaces the drawn version in place (no pile of takes); the clean
    // photo stays one step back in the history. A design the vendor made by
    // hand is theirs, and left alone.
    const baking = useRef<Set<string>>(new Set());
    useEffect(() => {
        const timer = window.setTimeout(() => {
            for (const s of board.shots) {
                if (s.still?.status !== 'ready' || !s.still.url || !s.still.assetUuid || baking.current.has(s.id)) continue;
                const designed = photoIsDesigned(s);
                if (designed && !s.design!.auto) continue;
                const want = hasOverlay(s);
                const key = overlayKey(s);
                if (!designed && !want) continue;
                if (designed && want && s.design!.overlayKey === key) continue;
                const shotId = s.id;
                if (designed && !want) {
                    // The words were all removed: back to the clean photo.
                    const d = s.design!;
                    setBoard((b) => ({ ...b, shots: b.shots.map((x) => (x.id !== shotId || !x.still ? x : {
                        ...x,
                        design: undefined,
                        still: { ...x.still, url: d.backgroundUrl, assetUuid: d.backgroundAssetUuid || x.still.assetUuid, takes: (x.still.takes || []).filter((tk) => tk.url !== d.backgroundUrl) },
                    })) }));
                    continue;
                }
                const background = designed ? s.design!.backgroundUrl : s.still.url;
                const backgroundAssetUuid = designed ? s.design!.backgroundAssetUuid : s.still.assetUuid;
                const snapshot = s;
                baking.current.add(shotId);
                (async () => {
                    try {
                        const { blob, scene } = await bakeOverlay(snapshot, background, brand);
                        const asset: any = await studioApi.importImage(projectUuid, { file: new File([blob], `photo-text-${Date.now()}.png`, { type: 'image/png' }) });
                        if (!asset?.uuid || !asset?.url) return;
                        const design = { assetUuid: asset.uuid, backgroundUrl: background, backgroundAssetUuid, fabricJson: scene, auto: true, overlayKey: key };
                        const current = boardRef.current.shots.find((x) => x.id === shotId);
                        if (!current?.still) return;
                        if (designed) {
                            // Same photo, new words: replace the drawn version.
                            setBoard((b) => ({ ...b, shots: b.shots.map((x) => (x.id !== shotId || !x.still ? x : { ...x, design, still: { ...x.still, assetUuid: asset.uuid, url: asset.url } })) }));
                        } else {
                            prod.addVersion(shotId, { uuid: asset.uuid, url: asset.url }, { design });
                        }
                    } catch { /* the photo stays as it was; its words still show over it */ } finally {
                        baking.current.delete(shotId);
                    }
                })();
            }
        }, 900);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [board.shots]);

    // ── 3D captures for tile shots taken as objects ─────────────
    // A shot waiting for its capture gets one, one at a time: the Surface 3D
    // Studio builds the real pieces in the shot's pose, frames them as the
    // shot's camera says, and the photo is made from that capture.
    const [captureFailed, setCaptureFailed] = useState<Record<string, string>>({});
    const waitingCapture = board.shots.filter((s) => s.still?.status === 'waiting' && s.still.waitingFor === 'capture');
    const capturing = waitingCapture.find((s) => needsCapture(board, s) && !captureFailed[s.id]);
    useEffect(() => {
        // A capture that is in and current: make the photo.
        for (const s of waitingCapture) {
            if (!needsCapture(board, s)) void prod.makeStill(s.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [board.shots]);

    // ── 3D clips: the real tiles filmed by the studio itself ─────────────
    const [clipFailed, setClipFailed] = useState<Record<string, string>>({});
    // One quiet retry per 3D shot: a network blip (or the API restarting) made
    // five tile clips of one film fail at once (13 Oct 2026).
    const [studioTries, setStudioTries] = useState<Record<string, number>>({});
    const retryStudio = (id: string) => {
        if ((studioTries[id] || 0) >= 1) return false;
        window.setTimeout(() => setStudioTries((m) => ({ ...m, [id]: (m[id] || 0) + 1 })), 5000);
        return true;
    };
    const filming = board.shots.find((s) => s.clip?.status === 'waiting' && s.clip.waitingFor === 'capture' && !clipFailed[s.id]);
    const saveClip3d = useCallback(async (shotId: string, clip: { file: File; poster: Blob; width: number; height: number; seconds: number }) => {
        const video: any = await studioApi.importVideo(projectUuid, clip.file, { width: clip.width, height: clip.height, durationSec: clip.seconds, thumbnail: clip.poster, origin: 'upload' });
        const shot = boardRef.current.shots.find((s) => s.id === shotId);
        // The first frame is the shot's photo when it has none yet.
        if (shot && shot.still?.status !== 'ready') {
            const photo: any = await studioApi.importImage(projectUuid, { file: new File([clip.poster], `clip3d-${Date.now()}.jpg`, { type: 'image/jpeg' }) });
            if (photo?.uuid) prod.setStage(shotId, 'still', { status: 'ready', assetUuid: photo.uuid, url: photo.url });
        }
        prod.setStage(shotId, 'clip', { status: 'ready', assetUuid: video.uuid, url: video.url, durationSec: clip.seconds, takes: shot?.clip?.takes });
    }, [boardRef, prod, projectUuid, studioApi]);

    const saveDesign = useCallback(async (result: { fabricJson: any; pngBlob: Blob; width: number; height: number }) => {
        const d = designing;
        if (!d) return;
        setDesigning(null);
        try {
            const asset: any = await studioApi.importImage(projectUuid, { file: new File([result.pngBlob], `design-${Date.now()}.png`, { type: 'image/png' }) });
            if (asset?.uuid && asset?.url) {
                prod.addVersion(d.shotId, { uuid: asset.uuid, url: asset.url }, { design: { assetUuid: asset.uuid, backgroundUrl: d.backgroundUrl, fabricJson: result.fabricJson } });
            }
        } catch { /* the previous version stays on the card */ }
    }, [designing, prod, projectUuid, studioApi]);

    const runEdit = (tool: Parameters<typeof editImageRequest>[0], params: Parameters<typeof editImageRequest>[1]) => {
        if (!editing) return;
        const req = editImageRequest(tool, params, { width: editing.asset.dimensions?.width, height: editing.asset.dimensions?.height });
        if (req) prod.applyEdit(editing.shotId, req);
    };
    const [draft, setDraft] = useState('');
    const [pendingProducts, setPendingProducts] = useState<ProductInfo[]>([]);
    const [picker, setPicker] = useState<null | 'chat' | 'shot'>(null);
    const reference = useReference({ projectUuid, setBoard });
    const film = useFilm({ projectUuid, board, setBoard, brand });

    const uuids = useMemo(() => [...new Set([
        ...board.shots.flatMap((s) => s.productUuids),
        ...(board.chat || []).flatMap((m) => (m.attachments || []).map((a) => a.productUuid).filter(Boolean) as string[]),
    ])], [board.shots, board.chat]);
    const products = useProductInfo(uuids);
    const selected = board.shots.find((s) => s.id === selectedId) || null;

    const onProposal = useCallback((p: ArdiProposal) => {
        // A production still named after the first words of its brief takes
        // the short name Ardi gave it.
        const brief = (boardRef.current.brief || '').replace(/\s+/g, ' ').slice(0, 48);
        if (p.title && (!prod.projectName || prod.projectName === brief)) prod.rename(p.title);
        const changed = prod.applyOps(p.ops || []);
        const ids = (p.renderShotIds?.length ? p.renderShotIds : changed)
            .filter((id) => boardRef.current.shots.some((s) => s.id === id));
        if (p.render === 'stills') ids.forEach((id) => prod.makeStill(id));
        if (p.render === 'clips') {
            ids.forEach((id) => {
                const s = boardRef.current.shots.find((x) => x.id === id);
                // Only video shots are animated; a photo shot stays a photo.
                if (s?.kind !== 'video') return;
                // Tiles filmed in 3D, and shots with their own 3D take, need no photo first.
                if (clipIn3d(s) || isTakeShot(s)) { prod.makeClip(id); return; }
                if (s?.still?.status === 'ready') prod.makeClip(id);
                else prod.makeStill(id);
            });
        }
        if (p.render === 'film') {
            // The cut is made from what is on the board; anything without its
            // photo yet gets one first, and the film opens when they are in.
            for (const s of boardRef.current.shots) {
                if (s.still?.status !== 'ready' && s.still?.status !== 'pending') prod.makeStill(s.id);
            }
            setFilmWanted(true);
        }
    }, [prod, boardRef]);

    // A film asked for opens as soon as every photo is in (or has failed).
    const [filmWanted, setFilmWanted] = useState(false);
    useEffect(() => {
        if (!filmWanted || !board.shots.length) return;
        if (board.shots.some((s) => s.still?.status === 'pending')) return;
        setFilmWanted(false);
        if (board.shots.some((s) => s.still?.status === 'ready')) film.open(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filmWanted, board.shots]);

    const ardi = useArdi({ projectUuid, board, setBoard, selectedShotId: selectedId, onProposal, brand });

    const send = useCallback((text?: string) => {
        const message = (text ?? draft).trim();
        if (!message) return;
        const attachments = pendingProducts.map((p) => ({ kind: 'product' as const, label: p.name, url: p.thumbnailUrl, productUuid: p.uuid }));
        ardi.send(message, { attachments, products: pendingProducts.map((p) => ({ uuid: p.uuid, name: p.name })) });
        if (text === undefined) setDraft('');
        setPendingProducts([]);
    }, [ardi, draft, pendingProducts]);

    // A production opened from the home page arrives with its first request.
    const started = useRef(false);
    useEffect(() => {
        if (prod.loading || started.current) return;
        const start = (location.state as any)?.start as StudioStartState | undefined;
        if (!start) return;
        started.current = true;
        prod.keep();
        navigate(location.pathname, { replace: true, state: null });
        (async () => {
            if (start.products?.length) rememberProducts(start.products);
            if (start.referenceFile) await reference.attach(start.referenceFile);
            if (start.message) {
                ardi.send(start.message, {
                    attachments: [
                        ...(start.products || []).map((p) => ({ kind: 'product' as const, label: p.name, url: p.thumbnailUrl, productUuid: p.uuid })),
                        ...(start.referenceFile ? [{ kind: 'reference' as const, label: start.referenceFile.name }] : []),
                    ],
                    products: (start.products || []).map((p) => ({ uuid: p.uuid, name: p.name })),
                });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prod.loading]);

    const addShot = () => {
        const last = board.shots[board.shots.length - 1];
        const shot: StudioShot = {
            id: newShotId(),
            productUuids: last?.productUuids || [],
            scene: last?.scene || '',
            camera: 'three-quarter',
            kind: last?.kind || 'image',
            ...(last?.kind === 'video' ? { motion: 'push-in' as const, durationSec: 4 } : {}),
        };
        setBoard((b) => ({ ...b, shots: [...b.shots, shot] }));
        setSelectedId(shot.id);
    };

    const duplicate = (s: StudioShot) => {
        const copy: StudioShot = { ...s, id: newShotId(), title: s.title, still: undefined, clip: undefined };
        setBoard((b) => {
            const i = b.shots.findIndex((x) => x.id === s.id);
            const shots = [...b.shots];
            shots.splice(i + 1, 0, copy);
            return { ...b, shots };
        });
        setSelectedId(copy.id);
    };

    const remove = (id: string) => {
        setBoard((b) => ({ ...b, shots: b.shots.filter((s) => s.id !== id) }));
        if (selectedId === id) setSelectedId(null);
    };

    const needPhotos = board.shots.filter((s) => s.kind !== 'card' && s.productUuids.length && !s.still?.url && s.still?.status !== 'pending');
    const needFilm = board.shots.filter((s) => s.kind === 'video' && (s.still?.status === 'ready' || isTakeShot(s)) && !s.clip?.url && s.clip?.status !== 'pending');
    const filmable = board.shots.some((s) => s.still?.status === 'ready' || s.clip?.status === 'ready');
    const filmCost = needFilm.reduce((sum, s) => sum + (shotClipPrice(prod.prices, s) || 0), 0);

    // On a phone the page is one column at a time: the conversation, or the shots.
    const [mobileTab, setMobileTab] = useState<'chat' | 'shots'>('chat');
    const hadShots = useRef(false);
    useEffect(() => {
        // The board opens with shots, or Ardi's first shots arrive: show them.
        if (board.shots.length && !hadShots.current) setMobileTab('shots');
        hadShots.current = board.shots.length > 0;
    }, [board.shots.length]);
    // A reference being read shows on the shots side.
    useEffect(() => { if (reference.busy) setMobileTab('shots'); }, [reference.busy]);

    // The reference being read, in the middle of an empty board — and on into
    // "planning" while Ardi turns it into shots.
    const scanMedia = reference.draft
        ? { kind: reference.draft.kind, url: reference.draft.previewUrl, frameUrls: reference.draft.frameUrls, aspect: reference.draft.aspect }
        : board.reference ? { kind: board.reference.kind, url: board.reference.url, frameUrls: (board.reference.frames || []).map((f) => f.url), aspect: undefined } : null;
    const scanStage: ScanStage | null = !scanMedia || board.shots.length ? null
        : reference.status === 'probing' || reference.status === 'uploading' || reference.status === 'reading' ? reference.status
            : reference.status === 'error' ? 'error'
                : ardi.busy ? 'planning' : null;

    const chips: ComposerChip[] = [
        ...pendingProducts.map((p) => ({
            key: `p:${p.uuid}`, kind: 'product' as const, label: p.name, thumbnailUrl: p.thumbnailUrl,
            preview: p.imageUrl || p.thumbnailUrl ? { url: (p.imageUrl || p.thumbnailUrl)!, kind: 'image' as const, title: p.name } : undefined,
        })),
        ...(reference.busy ? [{ key: 'ref', kind: 'reference' as const, label: reference.name, busy: true }] : []),
    ];

    const starters = board.shots.length
        ? [
            t('STUDIO.START.MORE_CINEMATIC', 'Make it more cinematic'),
            t('STUDIO.START.ADD_DETAIL', 'Add two close-ups of the material'),
            t('STUDIO.START.FILM_ALL', 'Film all the photos I like'),
        ]
        : [
            t('STUDIO.START.REEL', 'A 15-second reel for my best-selling rug, cinematic'),
            t('STUDIO.START.WEEK', 'A week of Instagram posts for my new collection'),
            t('STUDIO.START.PHOTOS', 'Four lifestyle photos of a sofa in different rooms'),
        ];

    if (prod.loading) {
        return <div className="fixed inset-0 z-40 flex items-center justify-center bg-background"><Spinner className="size-6" /></div>;
    }
    if (prod.error) {
        return (
            <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-background">
                <p className="text-sm text-muted-foreground">{prod.error}</p>
                <Button variant="outline" onClick={() => navigate('/marketing/content-studio')}>{t('STUDIO.BACK', 'Back to Studio')}</Button>
            </div>
        );
    }

    return (
        // Full screen, like a workspace should be: only the production's own
        // toolbar, which carries the credits.
        <div className="fixed inset-0 z-40 flex min-w-0 flex-col bg-background">
            {/* ── production toolbar ────────────────────── */}
            <header className="flex h-14 shrink-0 items-center gap-1.5 border-b px-2 sm:px-3">
                <Button variant="ghost" size="icon-sm" onClick={() => navigate('/marketing/content-studio')} aria-label={t('STUDIO.BACK', 'Back to Studio')}>
                    <ArrowLeft className="size-4 rtl:rotate-180" />
                </Button>
                <Input
                    ref={nameRef}
                    value={prod.projectName}
                    onChange={(e) => prod.rename(e.target.value)}
                    aria-label={t('STUDIO.HOME.RENAME', 'Rename')}
                    className="h-8 w-full min-w-0 max-w-[18rem] border-transparent px-1.5 text-sm font-medium shadow-none hover:border-input focus-visible:border-input"
                />
                <div className="ms-1 hidden shrink-0 items-center gap-1.5 md:flex">
                    <FormatMenu value={board.format} onChange={(format) => setBoard((b) => ({ ...b, format }))} />
                    <LookMenu value={board.look} onChange={(look) => setBoard((b) => ({ ...b, look }))} />
                            <FilmLightingButton value={board.lookRef} onChange={(lookRef) => setBoard((b) => ({ ...b, lookRef }))} />
                </div>
                <div className="ms-auto flex shrink-0 items-center gap-1.5">
                    {needPhotos.length > 0 && (
                        <Button variant="outline" size="sm" className="hidden gap-1.5 xl:inline-flex" onClick={() => needPhotos.forEach((s) => prod.makeStill(s.id))}>
                            <Sparkles className="size-3.5" />
                            {t('STUDIO.MAKE_PHOTOS', { count: needPhotos.length, defaultValue: 'Make {{count}} photos' })}
                            {prod.prices && <span className="text-muted-foreground">· {needPhotos.length * prod.prices.still}</span>}
                        </Button>
                    )}
                    {needFilm.length > 0 && (
                        <Button variant="outline" size="sm" className="hidden gap-1.5 xl:inline-flex" onClick={() => needFilm.forEach((s) => prod.makeClip(s.id))}>
                            <Film className="size-3.5" />
                            {t('STUDIO.FILM_SHOTS', 'Film {{n}} shots', { n: needFilm.length })}
                            <span className="text-muted-foreground">· {filmCost}</span>
                        </Button>
                    )}
                    <Button variant="outline" size="sm" className="gap-1.5" disabled={!filmable || film.preparing} onClick={() => film.open()}>
                        {film.preparing ? <Spinner className="size-3.5" /> : <Clapperboard className="size-3.5" />}
                        <span className="hidden sm:inline">{t('STUDIO.MAKE_FILM', 'Make the film')}</span>
                    </Button>
                    <Button variant="outline" size="sm" className="hidden gap-1.5 lg:inline-flex" onClick={() => navigate(`/marketing/content-studio/${projectUuid}/canvas`)}
                        title={t('STUDIO.CANVAS_HINT', 'The same production as nodes: every photo and film, with all the canvas tools')}>
                        <LayoutGrid className="size-3.5" />{t('STUDIO.CANVAS', 'Canvas')}
                    </Button>
                    <div className="mx-1 hidden h-5 w-px bg-border sm:block" />
                    <div className="hidden sm:block"><CreditBadge balance={prod.balance} compact /></div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={t('STUDIO.MORE', 'More')}><MoreHorizontal className="size-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                            {needPhotos.length > 0 && (
                                <DropdownMenuItem className="xl:hidden" onSelect={() => needPhotos.forEach((s) => prod.makeStill(s.id))}>
                                    <Sparkles className="size-4" />{t('STUDIO.MAKE_PHOTOS', { count: needPhotos.length, defaultValue: 'Make {{count}} photos' })}
                                    {prod.prices && <span className="ms-auto text-xs text-muted-foreground">{needPhotos.length * prod.prices.still}</span>}
                                </DropdownMenuItem>
                            )}
                            {needFilm.length > 0 && (
                                <DropdownMenuItem className="xl:hidden" onSelect={() => needFilm.forEach((s) => prod.makeClip(s.id))}>
                                    <Film className="size-4" />{t('STUDIO.FILM_SHOTS', 'Film {{n}} shots', { n: needFilm.length })}
                                    <span className="ms-auto text-xs text-muted-foreground">{filmCost}</span>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="lg:hidden" onSelect={() => navigate(`/marketing/content-studio/${projectUuid}/canvas`)}>
                                <LayoutGrid className="size-4" />{t('STUDIO.CANVAS', 'Canvas')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => requestAnimationFrame(() => { nameRef.current?.focus(); nameRef.current?.select(); })}>
                                <Pencil className="size-4" />{t('STUDIO.HOME.RENAME', 'Rename')}
                            </DropdownMenuItem>
                            {board.edit?.composition && (
                                <DropdownMenuItem onSelect={() => film.open(true)}><Film className="size-4" />{t('STUDIO.REBUILD_FILM', 'Rebuild the film from the board')}</DropdownMenuItem>
                            )}
                            {board.edit?.renderedUrl && (
                                <DropdownMenuItem asChild><a href={board.edit.renderedUrl} target="_blank" rel="noreferrer" download><Clapperboard className="size-4" />{t('STUDIO.DOWNLOAD_FILM', 'Download the film')}</a></DropdownMenuItem>
                            )}
                            <DropdownMenuItem onSelect={() => openSupport()}><Headset className="size-4" />{t('STUDIO.SUPPORT', 'Contact support')}</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onSelect={() => setConfirmDelete(true)}>
                                <Trash2 className="size-4" />{t('STUDIO.DELETE_PRODUCTION', 'Delete production')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>
            <ConfirmDeleteDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
                title={t('STUDIO.HOME.DELETE_TITLE', 'Delete “{{name}}”?', { name: prod.projectName || t('STUDIO.UNTITLED', 'New production') })}
                description={t('STUDIO.HOME.DELETE_BODY', 'Its shots, photos and videos are deleted with it. This can’t be undone.')}
                confirmLabel={t('STUDIO.HOME.DELETE_CONFIRM', 'Delete production')}
                onConfirm={async () => { await prod.remove(); navigate('/marketing/content-studio'); }}
            />

            <div className="flex min-h-0 flex-1">
                {/* ── Ardi ──────────────────────────────── */}
                <aside className={cn('min-h-0 w-full shrink-0 flex-col border-e lg:flex lg:w-[360px]', mobileTab === 'chat' ? 'flex' : 'hidden')}>
                    <ArdiPanel
                        messages={ardi.messages}
                        busy={ardi.busy}
                        activity={reference.busy ? t('STUDIO.ARDI.ACT_REFERENCE', 'Watching your reference…') : ardi.activity}
                        draft={draft}
                        onDraft={setDraft}
                        onSend={send}
                        onStop={ardi.stop}
                        chips={chips}
                        onRemoveChip={(key) => setPendingProducts((ps) => ps.filter((p) => `p:${p.uuid}` !== key))}
                        onAttachReference={(file) => reference.attach(file)}
                        onPickProducts={() => setPicker('chat')}
                        reference={board.reference}
                        products={products}
                        onMentionProduct={(p) => { rememberProducts([p]); setPendingProducts((ps) => (ps.some((x) => x.uuid === p.uuid) ? ps : [...ps, p])); }}
                        starters={starters}
                    />
                </aside>

                {/* ── board ─────────────────────────────── */}
                <main className={cn('min-w-0 flex-1 overflow-y-auto bg-muted/30 lg:block', mobileTab === 'shots' ? 'block' : 'hidden')}>
                    <div className="mx-auto max-w-6xl space-y-4 p-3 pb-24 sm:p-6 lg:pb-6">
                        {/* On a phone the format and look sit at the top of the shots. */}
                        <div className="flex items-center gap-1.5 md:hidden">
                            <FormatMenu value={board.format} onChange={(format) => setBoard((b) => ({ ...b, format }))} compact />
                            <LookMenu value={board.look} onChange={(look) => setBoard((b) => ({ ...b, look }))} />
                            <FilmLightingButton value={board.lookRef} onChange={(lookRef) => setBoard((b) => ({ ...b, lookRef }))} />
                        </div>
                        {scanStage && scanMedia ? (
                            <ReferenceScan
                                kind={scanMedia.kind}
                                url={scanMedia.url}
                                frameUrls={scanMedia.frameUrls}
                                aspect={scanMedia.aspect}
                                stage={scanStage}
                                error={reference.error}
                                onRemove={reference.clear}
                            />
                        ) : (
                        <>
                        {board.reference && (
                            <ReferenceStrip
                                reference={board.reference}
                                status={reference.status}
                                error={reference.error}
                                onRemove={reference.clear}
                                onRecreate={() => send(t('STUDIO.REF.RECREATE_MSG', 'Recreate my reference with my products'))}
                                busy={ardi.busy}
                                previewUrl={reference.draft?.previewUrl}
                            />
                        )}
                        {board.shots.length === 0 ? (
                            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-background px-6 py-16 text-center sm:py-20">
                                {ardi.busy ? <Spinner className="mb-3 size-5" /> : <Sparkles className="mb-3 size-5 text-muted-foreground" />}
                                <div className="text-sm font-medium text-foreground">
                                    {ardi.busy ? t('STUDIO.EMPTY.PLANNING', 'Ardi is planning your shots…') : t('STUDIO.EMPTY.TITLE', 'Your shots will appear here')}
                                </div>
                                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                                    {t('STUDIO.EMPTY.HINT', 'Ask Ardi on the left, or build it yourself shot by shot.')}
                                </p>
                                <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={addShot}><Plus className="size-3.5" />{t('STUDIO.ADD_SHOT', 'Add a shot')}</Button>
                            </div>
                        ) : (
                            <div className="grid gap-3 sm:gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${board.format === '16:9' ? 300 : board.format === '9:16' ? 160 : 200}px), 1fr))` }}>
                                {board.shots.map((s, i) => (
                                    <ShotCard
                                        key={s.id}
                                        shot={s}
                                        index={i}
                                        isKey={board.shots.some((x) => x.basedOn === s.id)}
                                        fromShot={s.basedOn && board.shots.some((x) => x.id === s.basedOn) ? board.shots.findIndex((x) => x.id === s.basedOn) + 1 : undefined}
                                        format={board.format}
                                        selected={s.id === selectedId}
                                        products={products}
                                        stillPrice={prod.prices?.still ?? null}
                                        clipPrice={shotClipPrice(prod.prices, s)}
                                        onSelect={() => setSelectedId(s.id === selectedId ? null : s.id)}
                                        onMakeStill={() => prod.makeStill(s.id)}
                                        onMakeClip={() => prod.makeClip(s.id)}
                                        onMakeReal={() => prod.makeReal(s.id)}
                                        realPrice={takePrice(prod.prices, s.clip?.durationSec || s.durationSec || 4)}
                                        onDuplicate={() => duplicate(s)}
                                        onDelete={() => remove(s.id)}
                                        onPreview={() => setPreviewIndex(i)}
                                        onEditTool={(tool) => openEditor(s.id, tool)}
                                        onDesign={() => openDesign(s.id)}
                                        onAngle={(p) => prod.applyEdit(s.id, angleRequest(p))}
                                        onUpscale={() => prod.applyEdit(s.id, { recipeSlug: 'upscale-image', inputs: { scale: 2 } })}
                                        brand={brand}
                                    />
                                ))}
                                <button type="button" onClick={addShot}
                                    className="flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed text-sm text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">
                                    <Plus className="size-4" />{t('STUDIO.ADD_SHOT', 'Add a shot')}
                                </button>
                            </div>
                        )}
                        {board.edit?.renderedUrl && (
                            <div className="flex items-center gap-3 rounded-xl border bg-background p-3">
                                <video src={board.edit.renderedUrl} className="h-24 rounded-md bg-black" muted playsInline controls />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-foreground">{t('STUDIO.FILM_READY', 'Your film is ready')}</div>
                                    <div className="text-xs text-muted-foreground">{t('STUDIO.FILM_READY_HINT', 'Download it, or reopen the edit to change anything.')}</div>
                                </div>
                                <Button variant="outline" size="sm" asChild><a href={board.edit.renderedUrl} download target="_blank" rel="noreferrer">{t('STUDIO.DOWNLOAD', 'Download')}</a></Button>
                                <Button variant="outline" size="sm" onClick={() => film.open()}>{t('STUDIO.EDIT_FILM', 'Edit')}</Button>
                            </div>
                        )}
                        </>
                        )}
                    </div>
                </main>

                {/* ── inspector ─────────────────────────── */}
                {selected && (
                    // A sheet over everything on a phone; a column on a desk.
                    <aside className="fixed inset-0 z-50 bg-background animate-in slide-in-from-bottom-4 fade-in duration-200 lg:static lg:z-auto lg:w-[400px] lg:shrink-0 lg:animate-none lg:border-s">
                        <ShotInspector
                            board={board}
                            shot={selected}
                            index={board.shots.findIndex((s) => s.id === selected.id)}
                            products={products}
                            prices={prod.prices}
                            editPrices={editPrices}
                            onChange={(patch) => prod.updateShot(selected.id, patch)}
                            onClose={() => setSelectedId(null)}
                            onPickProducts={() => setPicker('shot')}
                            onMakeStill={() => prod.makeStill(selected.id)}
                            onMakeClip={() => prod.makeClip(selected.id)}
                            onMakeReal={() => prod.makeReal(selected.id)}
                            realPrice={takePrice(prod.prices, selected.clip?.durationSec || selected.durationSec || 4)}
                            onCapture3d={async (file) => (await studioApi.uploadStyleRef(file)).url}
                            onEditTool={(tool) => openEditor(selected.id, tool)}
                            onDesign={() => openDesign(selected.id)}
                            onAngle={(p) => prod.applyEdit(selected.id, angleRequest(p))}
                            onUpscale={() => prod.applyEdit(selected.id, { recipeSlug: 'upscale-image', inputs: { scale: 2 } })}
                            onUseTake={(stage, uuid) => prod.useTake(selected.id, stage, uuid)}
                            onPreview={() => setPreviewIndex(board.shots.findIndex((s) => s.id === selected.id))}
                            onUploadFraming={async (file) => {
                                try { return (await studioApi.uploadStyleRef(file))?.url ?? null; } catch { return null; }
                            }}
                        />
                    </aside>
                )}
            </div>

            {/* ── phone: switch between the conversation and the shots ── */}
            <nav className="grid h-14 shrink-0 grid-cols-2 border-t bg-background pb-[env(safe-area-inset-bottom)] lg:hidden">
                {([
                    { id: 'chat' as const, icon: <MessageCircle className="size-4" />, label: t('STUDIO.MOBILE.ARDI', 'Ardi'), busy: ardi.busy },
                    { id: 'shots' as const, icon: <LayoutGrid className="size-4" />, label: board.shots.length ? t('STUDIO.MOBILE.SHOTS_N', 'Shots · {{n}}', { n: board.shots.length }) : t('STUDIO.MOBILE.SHOTS', 'Shots'), busy: false },
                ]).map((x) => (
                    <button key={x.id} type="button" data-mobile-tab={x.id} onClick={() => setMobileTab(x.id)}
                        className={cn('flex cursor-pointer items-center justify-center gap-2 text-sm transition-colors', mobileTab === x.id ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                        {x.busy ? <Spinner className="size-4" /> : x.icon}{x.label}
                    </button>
                ))}
            </nav>

            {!capturing && filming && (
                <Surface3DStudioWidget
                    key={`clip:${filming.id}:${studioTries[filming.id] || 0}`}
                    productUuid={filming.productUuids[0]}
                    applicationTypes={['floor', 'walls']}
                    mode="single"
                    lockMode
                    poseId={filming.pose || 'single'}
                    aspectRatio={board.format}
                    onCapture={async (file) => (await studioApi.uploadStyleRef(file)).url}
                    onSnapshotUpdated={() => undefined}
                    headlessAngle={objectAngleFor(filming)}
                    headlessClip={{ move: move3dFor(filming), seconds: Math.max(2, filming.durationSec || 3), fps: 30, ...clipSize(board.format), backdrop: filming.backdrop }}
                    onClipRecorded={(clip) => saveClip3d(filming.id, clip)}
                    onHeadlessDone={(err) => {
                        if (!err || retryStudio(filming.id)) return;
                        setClipFailed((m) => ({ ...m, [filming.id]: err }));
                        prod.setStage(filming.id, 'clip', { ...filming.clip, status: 'failed', waitingFor: undefined, error: `The 3D studio could not film the tiles: ${err}` });
                    }}
                />
            )}

            {capturing && (
                <Surface3DStudioWidget
                    key={`${capturing.id}:${captureKey(board, capturing)}:${studioTries[capturing.id] || 0}`}
                    productUuid={capturing.productUuids[0]}
                    applicationTypes={['floor', 'walls']}
                    mode="single"
                    lockMode
                    poseId={capturing.pose || 'single'}
                    aspectRatio={board.format}
                    onCapture={async (file) => (await studioApi.uploadStyleRef(file)).url}
                    headlessAngle={objectAngleFor(capturing)}
                    onSnapshotUpdated={(url) => prod.updateShot(capturing.id, { framingRef: { url, source: 'studio3d', key: captureKey(board, capturing) } })}
                    onHeadlessDone={(err) => {
                        if (!err || retryStudio(capturing.id)) return;
                        setCaptureFailed((m) => ({ ...m, [capturing.id]: err }));
                        prod.setStage(capturing.id, 'still', { ...capturing.still, status: 'failed', waitingFor: undefined, error: `The 3D studio could not pose the tiles: ${err}` });
                    }}
                />
            )}

            <ProductPickerPanel
                open={picker !== null}
                onClose={() => setPicker(null)}
                anchor={picker === 'shot' ? 'beside-panel' : 'start'}
                onDrop={(items: any[]) => {
                    const infos: ProductInfo[] = items.map(productInfoFrom);
                    rememberProducts(infos);
                    if (picker === 'shot' && selected) {
                        prod.updateShot(selected.id, { productUuids: [...new Set([...selected.productUuids, ...infos.map((p) => p.uuid)])] });
                    } else {
                        setPendingProducts((ps) => [...ps, ...infos.filter((p) => !ps.some((x) => x.uuid === p.uuid))]);
                    }
                    setPicker(null);
                }}
            />

            {designing && (
                <DesignEditorModal
                    open
                    backgroundUrl={designing.backgroundUrl}
                    initialFabricJson={designing.fabricJson}
                    brandKit={brand}
                    onClose={() => setDesigning(null)}
                    onSave={saveDesign}
                />
            )}

            {editing && (
                <ImageEditorModal
                    open
                    asset={editing.asset}
                    initialTool={editing.tool}
                    onClose={() => setEditing(null)}
                    saveLabel={t('STUDIO.EDIT.APPLY', 'Apply')}
                    onSaveExpand={(p) => runEdit('expand', { expand: p })}
                    onSaveRetouch={(p) => runEdit('retouch', { retouch: p })}
                    onSaveModify={(p) => runEdit('modify', { modify: p })}
                    onSaveGraphics={(p) => runEdit('graphics', { graphics: p })}
                    onUploadReferenceImage={async (file) => (await studioApi.uploadStyleRef(file)).url}
                    expandCreditCost={editPrices['expand-image'] ?? 8}
                    retouchCreditCost={editPrices['retouch-image'] ?? 10}
                    modifyCreditCost={editPrices['modify-image'] ?? 10}
                    graphicsCreditCost={editPrices['graphics-image'] ?? 13}
                />
            )}

            <ShotPreview shots={board.shots} index={previewIndex} onIndex={setPreviewIndex} onUseTake={prod.useTake} />

            {film.composition && (
                <VideoEditorModal
                    open
                    projectUuid={projectUuid}
                    initialComposition={film.composition}
                    onChange={film.onChange}
                    onClose={film.close}
                    onExport={film.runExport}
                    exportState={film.exportState}
                    onCancelExport={film.cancelExport}
                    onDismissExport={film.dismissExport}
                />
            )}
            {film.exportState?.status === 'running' && !film.composition && (
                <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm shadow-sm">
                    <Spinner className="size-4" />{t('STUDIO.EXPORTING', 'Rendering your film… {{p}}%', { p: Math.round((film.exportState.progress?.progress || 0) * 100) })}
                    <Button variant="ghost" size="icon-sm" onClick={film.cancelExport} aria-label={t('STUDIO.STOP', 'Stop')}><X className="size-3.5" /></Button>
                </div>
            )}
        </div>
    );
};
